import { fhirClients } from "src-next/shared/api/fhirClient";
import { DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID } from "src-next/modules/crm-clinical/messageTemplates/constants";
import { messageTemplatesService } from "src-next/modules/crm-clinical/messageTemplates/services/messageTemplatesService";
import { filterMessageTemplatesByOrganization } from "src-next/modules/crm-clinical/messageTemplates/utils/messageTemplateUiUtils";
import { questionnaireManagementService } from "src-next/modules/questionnaire-builder/questionnaire-management/services";

import {
  buildAutomationAuthorReference,
  buildAutomationSourceReference,
  normalizeSearchTerm,
} from "../helpers/automationHelpers";
import { resolveQuestionnaireFromTemplate } from "../helpers/automationTemplateUtils";
import { AUTOMATION_CONFIG_QUESTIONNAIRE_ID } from "../constants/automationFhirConstants";
import { isSystemDefaultAutomation, compareAutomationDisplayOrder } from "../constants/automationUiConstants";
import {
  getAutomationIdFromQuestionnaireResponse,
  toAutomationConfigFromQuestionnaireResponse,
  toQuestionnaireResponseFromAutomationConfig,
} from "../transformers/automationFhirTransformers";
import type {
  AutomationConfig,
  AutomationContext,
  AutomationFilters,
  AutomationItem,
  AutomationId,
} from "../types";

function matchSearch(item: AutomationItem, search: string): boolean {
  if (!search) return true;

  const haystack = `${item.title} ${item.description} ${item.triggerMoment ?? ""}`.toLocaleLowerCase();
  return haystack.includes(search);
}

interface ListByContextParams {
  context: AutomationContext;
  filters?: AutomationFilters;
}

interface RemoveConfigByAutomationIdParams {
  context: AutomationContext;
  automationId: AutomationId;
}

interface GetConfigByAutomationIdParams {
  context: AutomationContext;
  automationId: AutomationId;
}

interface UpsertConfigParams {
  context: AutomationContext;
  config: AutomationConfig;
}

const AUTOMATION_CATALOG: AutomationItem[] = [
  {
    id: "confirmation",
    title: "Confirmação",
    description:
      "O paciente receberá a mensagem padrão sempre que uma consulta for marcada.",
    isEnabled: false,
    isConfigured: false,
  },
  {
    id: "cancellation",
    title: "Cancelamento",
    description:
      "O paciente receberá a mensagem padrão sempre que uma consulta for cancelada.",
    isEnabled: false,
    isConfigured: false,
  },
  {
    id: "payment",
    title: "Pagamento",
    description:
      "O paciente receberá a mensagem padrão de pagamento sempre que uma cobrança for gerada para a consulta.",
    isEnabled: false,
    isConfigured: false,
  },
  {
    id: "triage",
    title: "Triagem",
    description:
      "Envia mensagem automática quando um paciente realizar um agendamento pela agenda externa.",
    triggerMoment:
      "Quando um paciente realizar um agendamento pela agenda externa.",
    isEnabled: false,
    isConfigured: false,
  },
  {
    id: "preConsultation",
    title: "Pré-Consulta",
    description:
      "Envia mensagem automática quando a consulta for confirmada.",
    triggerMoment: "Quando a consulta for confirmada.",
    isEnabled: false,
    isConfigured: false,
  },
  {
    id: "postConsultation",
    title: "Pós-Consulta",
    description:
      "Envia mensagem automática quando o atendimento do paciente for finalizado.",
    triggerMoment: "Quando o atendimento do paciente for finalizado.",
    isEnabled: false,
    isConfigured: false,
  },
  {
    id: "birthday",
    title: "Aniversariantes",
    description:
      "Envia mensagem de parabéns aos pacientes aniversariantes do dia.",
    triggerMoment:
      "Disparo manual pelo dashboard profissional para aniversariantes do dia.",
    isEnabled: false,
    isConfigured: false,
  },
];

function getQuestionnaireResponseClient() {
  return fhirClients.admin.resource<fhir5.QuestionnaireResponse>(
    "QuestionnaireResponse",
  );
}

async function listConfigsByContext(params: {
  context: AutomationContext;
}): Promise<AutomationConfig[]> {
  const authorReference = buildAutomationAuthorReference(params.context);
  const client = getQuestionnaireResponseClient();

  const bundle = await client.search({
    questionnaire: AUTOMATION_CONFIG_QUESTIONNAIRE_ID,
    author: authorReference,
  });

  return (bundle.entry ?? [])
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is fhir5.QuestionnaireResponse =>
        resource !== undefined &&
        resource.resourceType === "QuestionnaireResponse",
    )
    .map((resource) => toAutomationConfigFromQuestionnaireResponse(resource))
    .filter((config): config is AutomationConfig => config !== null);
}

async function listByContext(
  params: ListByContextParams,
): Promise<AutomationItem[]> {
  const { context, filters = {} } = params;
  const search = normalizeSearchTerm(filters.search);
  const whatsappBusinessAccountId =
    context.whatsappBusinessAccountId?.trim() || DEFAULT_WHATSAPP_BUSINESS_ACCOUNT_ID;

  const [configs, rawMessageTemplates, questionnaires] = await Promise.all([
    listConfigsByContext({ context }),
    messageTemplatesService.list({
      whatsappBusinessAccountId,
    }),
    questionnaireManagementService.listByOrganizationIdentifier(
      context.organizationId,
    ),
  ]);
  const messageTemplates = filterMessageTemplatesByOrganization(
    rawMessageTemplates,
    context.organizationId,
  );
  const messageTemplateById = new Map(
    messageTemplates.map((messageTemplate) => [
      messageTemplate.id,
      messageTemplate,
    ]),
  );
  const questionnaireNameById = new Map(
    questionnaires
      .filter((questionnaire) => Boolean(questionnaire.id))
      .map((questionnaire) => [
        questionnaire.id as string,
        questionnaire.title,
      ]),
  );
  const configByAutomationId = new Map(
    configs.map((config) => [config.automationId, config] as const),
  );
  const configuredAutomationIds = new Set(
    configs.map((config) => config.automationId),
  );

  return AUTOMATION_CATALOG.map((automation) => {
    const hasConfig = configuredAutomationIds.has(automation.id);
    const config = configByAutomationId.get(automation.id);
    const messageTemplateId = config?.messageTemplateId;
    const linkedMessageTemplate = messageTemplateId
      ? messageTemplateById.get(messageTemplateId)
      : undefined;
    const { questionnaireId, questionnaireName } = resolveQuestionnaireFromTemplate(
      linkedMessageTemplate,
      questionnaireNameById,
    );

    const isSystemDefault = isSystemDefaultAutomation(automation.id);

    return {
      ...automation,
      // Padrões do sistema permanecem ativos mesmo sem configuração customizada salva.
      isEnabled: isSystemDefault ? true : hasConfig,
      isConfigured: hasConfig,
      messageTemplateId,
      messageTemplateName: linkedMessageTemplate?.name,
      questionnaireId,
      questionnaireName,
      linkedMessageTemplate,
    };
  })
    .filter((item) => matchSearch(item, search))
    .sort((itemA, itemB) =>
      compareAutomationDisplayOrder(itemA.id, itemB.id),
    );
}

async function searchAutomationConfigResourcesByContext(
  context: AutomationContext,
): Promise<fhir5.QuestionnaireResponse[]> {
  const client = getQuestionnaireResponseClient();
  const authorReference = buildAutomationAuthorReference(context);

  const bundle = await client.search({
    questionnaire: AUTOMATION_CONFIG_QUESTIONNAIRE_ID,
    author: authorReference,
  });

  return (bundle.entry ?? [])
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is fhir5.QuestionnaireResponse =>
        resource !== undefined &&
        resource.resourceType === "QuestionnaireResponse",
    );
}

async function removeConfigByAutomationId(
  params: RemoveConfigByAutomationIdParams,
): Promise<void> {
  const { context, automationId } = params;
  const resources = await searchAutomationConfigResourcesByContext(context);

  const targetResource = resources.find((resource) => {
    const id = getAutomationIdFromQuestionnaireResponse(resource);
    return id === automationId;
  });

  if (!targetResource?.id) return;
  await getQuestionnaireResponseClient().remove(targetResource.id);
}

/** Verifica se existem automações personalizadas salvas para o contexto. */
async function hasAutomationConfigsByContext(params: {
  context: AutomationContext;
}): Promise<boolean> {
  const configs = await listConfigsByContext(params);
  return configs.length > 0;
}

/** Remove todas as configurações de automação salvas (QuestionnaireResponse). */
async function removeAllConfigsByContext(params: {
  context: AutomationContext;
}): Promise<void> {
  const resources = await searchAutomationConfigResourcesByContext(
    params.context,
  );
  const client = getQuestionnaireResponseClient();

  await Promise.all(
    resources
      .filter((resource) => Boolean(resource.id))
      .map((resource) => client.remove(resource.id as string)),
  );
}

async function getConfigByAutomationId(
  params: GetConfigByAutomationIdParams,
): Promise<AutomationConfig | null> {
  const { context, automationId } = params;
  const configs = await listConfigsByContext({ context });
  return configs.find((config) => config.automationId === automationId) ?? null;
}

async function upsertConfig(
  params: UpsertConfigParams,
): Promise<AutomationConfig> {
  const { context, config } = params;
  const client = getQuestionnaireResponseClient();
  const authorReference = buildAutomationAuthorReference(context);
  const sourceReference = buildAutomationSourceReference(context);

  const bundle = await client.search({
    questionnaire: AUTOMATION_CONFIG_QUESTIONNAIRE_ID,
    author: authorReference,
  });

  const existingResource = (bundle.entry ?? [])
    .map((entry) => entry.resource)
    .find((resource) => {
      if (!resource || resource.resourceType !== "QuestionnaireResponse")
        return false;
      const id = getAutomationIdFromQuestionnaireResponse(
        resource as fhir5.QuestionnaireResponse,
      );
      return id === config.automationId;
    }) as fhir5.QuestionnaireResponse | undefined;

  const payload = toQuestionnaireResponseFromAutomationConfig({
    config,
    authorReference,
    sourceReference,
    existingId: existingResource?.id,
  });

  if (existingResource?.id) {
    await client.update(existingResource.id, payload);
    return config;
  }

  await client.create(payload);
  return config;
}

export const automationService = {
  listByContext,
  removeConfigByAutomationId,
  removeAllConfigsByContext,
  hasAutomationConfigsByContext,
  getConfigByAutomationId,
  upsertConfig,
};
